// types/devices.ts
// Request/Response interfaces
export interface RegisterDeviceRequest {
    deviceName: string;
    locationId?: number;
    ipAddress?: string;
    serialNumber: string;
}

export interface RegisterDeviceResponse {
    deviceId: number;
    deviceToken: string;
}

export interface UpdateDeviceRequest {
    deviceName?: string;
    serialNumber?: string;  // ← Added this property
    locationId?: number;
    active?: boolean;
}

// System status and metrics interfaces
export interface SystemStatusResponse {
    deviceId: number;
    deviceName: string;
    location: string;
    currentPlaylist?: string;
    lastSync: string;
    metrics: SystemStatusMetrics;

    // Two independent verdicts, both computed by the API on every read.
    //
    // A player fails in two unrelated ways: the machine underneath it (hot,
    // browning out, out of disk, off the network) and the signage software on top
    // (stopped, crashed, waiting for something). They are reported by two
    // separate services on the Pi, over two endpoints, from two systemd units,
    // exactly so that one keeps arriving when the other has stopped.
    //
    // Collapsing them into one word is what made the old single status useless:
    // "the Pi is fine and Pinage is not running" looked identical to a device
    // somebody had unplugged.
    healthStatus: DeviceHealthStatus;
    healthReasons?: string[];
    lastHealthReport?: string;

    pinageStatus: PinageStatus;
    pinageReasons?: string[];
    lastPinageReport?: string;

    // Each Pinage service's one-word state - display, content, audience and
    // health - as system.py last reported it. Empty while that report is silent.
    services?: Record<string, string>;

    // Whether a screen is plugged in, from health.py - not the same question as
    // whether Pinage is showing anything on it.
    monitorConnected?: boolean | null;
    monitorConnector?: string | null;

    // The camera, from health.py, so it is known with Pinage stopped. No camera
    // is a grey note, never a warning: plenty of sites have none. Working is
    // null until audience.py has tried the camera; detected but not working is
    // a health reason, "Camera not working".
    cameraDetected?: boolean | null;
    cameraType?: 'csi' | 'usb' | null;
    cameraWorking?: boolean | null;

    boardModel?: string;
    pinageVersion?: string;
    stagedVersion?: string;
    pinageBuild?: string;
    stagedBuild?: string;
    updatePending?: boolean;
}

export interface SystemStatusMetrics {
    cpuUsage: number;
    memoryUsage: number;
    diskUsage: number;
    temperature?: number;
    uptimeSeconds?: number;
    signalDbm?: number;
}

// How the single board computer is.
export type DeviceHealthStatus =
    'Online' | 'Warning' | 'Offline' | 'Offline+15' | 'Offline+30' | 'Pending';

// What Pinage is doing. "Not running" is the reading that did not exist before
// the split: the Pi is demonstrably up and reporting, and it is the signage
// software specifically that has stopped - an SSH session rather than a site
// visit. "Unknown" means both reports have gone quiet, so we genuinely cannot
// say, and saying so is better than guessing.
export type PinageStatus =
    'Running' | 'Warning' | 'Not running' | 'Unknown' | 'Pending';

// Define a string literal type for device status
export type DeviceStatus = 'Online' | 'Offline' | 'Warning' | 'Maintenance' | 'Pending';

export type DeviceOnlineStatus = 'Online' | 'Offline' | 'Offline+15' | 'Offline+30' | 'Pending';

// Base device interface
export interface Device {
    deviceId: number;
    deviceName: string;
    serialNumber: string;
    locationId?: number;
    ipAddress: string;
    lastHeartbeat?: Date;
    lastSyncTime?: Date;
    status: DeviceStatus;
    onlineStatus: DeviceOnlineStatus;  // ADD THIS LINE
    cacheSize?: number;
    active?: boolean;
    currentStatus?: SystemStatusMetrics;
    location?: DeviceLocationInfo;
    deviceToken?: string;  // Optional since it might not always be included for security
    piConnectUrl?: string; // Add this - URL for Pi Connect registration

    // The same two verdicts as SystemStatusResponse, so the Devices page can show
    // them without a second round trip. Computed by the same evaluator on the API
    // side, so the two pages cannot drift into disagreeing about one device.
    healthStatus?: DeviceHealthStatus;
    healthReasons?: string[];
    pinageStatus?: PinageStatus;
    pinageReasons?: string[];
    lastPinageReport?: Date;

    // See SystemStatusResponse.cameraDetected.
    cameraDetected?: boolean | null;

    boardModel?: string;
    pinageVersion?: string;
    stagedVersion?: string;
    pinageBuild?: string;
    stagedBuild?: string;

    // Degrees clockwise, or null when rotation is left to the device's own
    // config.json - which every device starts as.
    displayRotation?: number | null;
}

    // ADD THIS NEW INTERFACE
export interface DeviceLocationInfo {
    locationName: string;
    city: string;
    address: string;
}

export interface AddDeviceRequest {
    deviceName: string;
    locationId?: number;
    username?: string;
    password?: string;
    enableSsh?: boolean;
    timezone?: string;
    keyboardLayout?: string;
    wifiSsid?: string;
    wifiPassword?: string;
    wifiCountry?: string;
}

export interface AddDeviceResponse {
    deviceId: number;
    deviceToken: string;
    deviceName: string;
    companyId: number;
    jwtToken: string;  // ADD THIS LINE
}

// Helper function to check if a device is considered online
export const isDeviceOnline = (onlineStatus: DeviceOnlineStatus): boolean => {
    return onlineStatus === 'Online';
};

// Helper function to get a user-friendly display for online status
export const getOnlineStatusDisplay = (onlineStatus: DeviceOnlineStatus): string => {
    switch (onlineStatus) {
        case 'Online':
            return 'Online';
        case 'Pending':
            return 'Pending';
        case 'Offline':
            return 'Offline (5-15 min)';
        case 'Offline+15':
            return 'Offline (15-30 min)';
        case 'Offline+30':
            return 'Offline (30+ min)';
        default:
            return onlineStatus;
    }
};

// Helper function to get color class for online status
export const getOnlineStatusColor = (onlineStatus: DeviceOnlineStatus): string => {
    switch (onlineStatus) {
        case 'Online':
            return 'text-green-600';
        case 'Offline':
            return 'text-orange-600';
        case 'Offline+15':
            return 'text-red-500';
        case 'Offline+30':
            return 'text-red-700';
        case 'Pending':
            return 'text-gray-500';
        default:
            return 'text-gray-600';
    }
};